import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { OurSquareLogo } from '../../components/brand/Logo';
import { 
  Trophy, CheckCircle, Circle, Clock,
  FileText, Briefcase, Heart, Home, GraduationCap, Award,
  Users
} from 'lucide-react';

const MILESTONE_ICONS = {
  intake_completed: FileText,
  orientation_completed: Users,
  house_rules_signed: Home,
  financial_literacy: GraduationCap,
  employment_readiness: Briefcase,
  recovery_support: Heart,
  lease_readiness: Home,
  program_completion: Award,
};

const CATEGORY_COLORS = {
  onboarding: 'bg-blue-100 text-blue-700',
  education: 'bg-purple-100 text-purple-700',
  recovery: 'bg-pink-100 text-pink-700',
  transition: 'bg-amber-100 text-amber-700',
  completion: 'bg-emerald-100 text-emerald-700',
};

export default function ResidentMilestonesPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMilestones();
  }, []);

  const loadMilestones = async () => {
    setLoading(true);
    try {
      // First get the resident record for this user
      const housesRes = await api.get('/housing/entity/our_square/houses');
      const houses = housesRes.data || [];
      
      // Find the resident record for the current user
      let residentId = null;
      for (const house of houses) {
        const resident = (house.residents || []).find(r => r.user_id === user.id);
        if (resident) {
          residentId = resident.id;
          break;
        }
      }

      if (residentId) {
        const res = await api.get(`/milestones/resident/${residentId}`);
        setData(res.data);
      } else {
        // If no resident record found, show definitions with no progress
        const defsRes = await api.get('/milestones/definitions');
        setData({
          milestones: defsRes.data.map(m => ({ ...m, completed: false, completion_data: null })),
          completed_count: 0,
          total_count: defsRes.data.length,
          progress_percent: 0,
        });
      }
    } catch (e) { 
      // Show empty state with milestone definitions
      try {
        const defsRes = await api.get('/milestones/definitions');
        setData({
          milestones: defsRes.data.map(m => ({ ...m, completed: false, completion_data: null })),
          completed_count: 0,
          total_count: defsRes.data.length,
          progress_percent: 0,
        });
      } catch (e2) { console.error(e2); }
    }
    setLoading(false);
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">Unable to load milestones</div>;

  return (
    <div className="space-y-6" data-testid="resident-milestones-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <OurSquareLogo size="sm" className="h-12 w-auto" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Program Milestones</h1>
          <p className="text-sm text-muted-foreground">Track your progress through the housing program</p>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-cyan-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-4xl font-bold text-cyan-700">{data.progress_percent}%</p>
              <p className="text-sm text-muted-foreground">Program Complete</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900">{data.completed_count}/{data.total_count}</p>
              <p className="text-sm text-muted-foreground">Milestones Achieved</p>
            </div>
          </div>
          <Progress value={data.progress_percent} className="h-4" />
          
          {data.progress_percent === 100 && (
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg text-center">
              <Award className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              <p className="font-semibold text-emerald-800">Congratulations! You've completed the program!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Milestones List */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your Journey</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.milestones.map((m, idx) => {
              const Icon = MILESTONE_ICONS[m.key] || Trophy;
              const isCompleted = m.completed;
              const isNext = !isCompleted && idx === data.milestones.findIndex(ms => !ms.completed);
              
              return (
                <div key={m.key} className={`relative flex items-start gap-4 p-4 rounded-xl transition-colors ${isCompleted ? 'bg-emerald-50' : isNext ? 'bg-cyan-50 border-2 border-cyan-200' : 'bg-slate-50'}`}>
                  {/* Connection Line */}
                  {idx < data.milestones.length - 1 && (
                    <div className={`absolute left-8 top-16 w-0.5 h-8 ${isCompleted ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                  )}
                  
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-emerald-500 text-white' : isNext ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {isCompleted ? <CheckCircle size={24} /> : <Icon size={24} />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold ${isCompleted ? 'text-emerald-700' : isNext ? 'text-cyan-700' : 'text-slate-700'}`}>{m.name}</p>
                      <Badge className={`${CATEGORY_COLORS[m.category]} text-xs`}>{m.category}</Badge>
                      {isNext && <Badge className="bg-cyan-500 text-white text-xs">Next Step</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{m.description}</p>
                    
                    {isCompleted && m.completion_data && (
                      <div className="mt-3 p-3 bg-emerald-100 rounded-lg">
                        <p className="text-xs text-emerald-700">
                          <CheckCircle className="inline w-3 h-3 mr-1" />
                          Completed on {new Date(m.completion_data.approved_at).toLocaleDateString()}
                        </p>
                        {m.completion_data.notes && (
                          <p className="text-xs text-emerald-600 mt-1 italic">"{m.completion_data.notes}"</p>
                        )}
                      </div>
                    )}
                    
                    {isNext && (
                      <div className="mt-3 p-3 bg-cyan-100 rounded-lg">
                        <p className="text-xs text-cyan-700">
                          <Clock className="inline w-3 h-3 mr-1" />
                          Work with your case manager to complete this milestone
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Encouragement */}
      {data.progress_percent < 100 && data.progress_percent > 0 && (
        <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-6 text-center">
            <Trophy className="w-10 h-10 mx-auto mb-3 text-purple-500" />
            <p className="font-semibold text-purple-800">You're making great progress!</p>
            <p className="text-sm text-purple-600 mt-1">Keep going - every milestone brings you closer to independence.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
